import { buildBrowseHref } from '@/lib/browse-state';
import {
  resolveGeographySelectionFromUrl,
  type GeographySelection,
  type ResolvedGeographyLocation,
} from '@/lib/geography-navigation';
import type { PublicRouteKey } from '@/lib/public-route-config';

export type PublicPrimaryNavKey =
  | 'calendar'
  | 'classes'
  | 'locations'
  | 'creators'
  | 'search'
  | 'updates';

export interface GeographyLinks {
  basePath: string;
  calendarHref: string;
  classesHref: string;
  locationsHref: string | null;
  creatorsHref: string;
  searchHref: string;
  updatesHref: string | null;
}

export function getGeographyLinks(regionSlug: string, localitySlug?: string): GeographyLinks {
  const basePath = localitySlug
    ? `/regions/${regionSlug}/${localitySlug}`
    : `/regions/${regionSlug}`;

  return {
    basePath,
    calendarHref: `${basePath}/calendar`,
    classesHref: `${basePath}/classes`,
    locationsHref: regionSlug === 'online' ? null : `${basePath}/locations`,
    creatorsHref: `${basePath}/creators`,
    searchHref: buildBrowseHref('/search', {
      region: regionSlug,
      locality: localitySlug,
      sort: 'popular',
    }),
    updatesHref: regionSlug === 'online' ? null : `${basePath}/updates`,
  };
}

export function resolveShellGeographySelection(
  pathname: string,
  search: string,
  fallback: GeographySelection
): ResolvedGeographyLocation {
  const directResolution = resolveGeographySelectionFromUrl(pathname, search, fallback);
  if (directResolution.section !== 'unknown') {
    return directResolution;
  }

  const params = new URLSearchParams(search);
  const returnTo = params.get('returnTo');
  if (!returnTo) {
    return directResolution;
  }

  try {
    const decodedReturnTo = decodeURIComponent(returnTo);
    const resolvedUrl = new URL(decodedReturnTo, 'https://kalovate.local');
    const returnResolution = resolveGeographySelectionFromUrl(
      resolvedUrl.pathname,
      resolvedUrl.search,
      fallback
    );

    if (returnResolution.section !== 'unknown') {
      return returnResolution;
    }
  } catch {
    // Ignore malformed returnTo values and fall back to persisted geography.
  }

  return directResolution;
}

export function getPrimaryNavActiveKey(routeKey: PublicRouteKey): PublicPrimaryNavKey | null {
  switch (routeKey) {
    case 'region-calendar':
    case 'locality-calendar':
      return 'calendar';
    case 'classes':
    case 'class-detail':
      return 'classes';
    case 'locations':
    case 'location-detail':
      return 'locations';
    case 'creators':
    case 'creator-detail':
      return 'creators';
    case 'search':
      return 'search';
    case 'updates':
    case 'update-detail':
      return 'updates';
    default:
      return null;
  }
}
